import './style.css';
import './operation.css';
import './journey.css';
import './energy.css';
import './operations-control.css';
import './finale.css';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { initEuropeNetwork } from './scripts/europe-network';
import { initIntelligenceOnWheels } from './scripts/intelligence-on-wheels';
import { initTerminalDeparture } from './scripts/terminal-departure';
import { initOriginDestination } from './scripts/origin-destination';
import { initEnergySystem } from './scripts/energy-system';
import { initAlwaysMoving } from './scripts/always-moving';
import { initMoveBeyondLimits } from './scripts/move-beyond-limits';

gsap.registerPlugin(ScrollTrigger);

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const coarsePointer = window.matchMedia('(pointer: coarse)').matches;

const select = <T extends Element>(selector: string): T | null =>
  document.querySelector<T>(selector);

const selectAll = <T extends Element>(selector: string): T[] =>
  Array.from(document.querySelectorAll<T>(selector));

function initMenu(): void {
  const toggle = select<HTMLButtonElement>('.menu-toggle');
  const panel = select<HTMLElement>('#menu-panel');
  if (!toggle || !panel) return;

  const setOpen = (open: boolean): void => {
    toggle.setAttribute('aria-expanded', String(open));
    panel.setAttribute('aria-hidden', String(!open));
    panel.classList.toggle('is-open', open);
  };

  toggle.addEventListener('click', () => {
    setOpen(toggle.getAttribute('aria-expanded') !== 'true');
  });

  panel.addEventListener('click', (event) => {
    if ((event.target as Element).closest('a')) setOpen(false);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
      toggle.focus();
    }
  });

  document.addEventListener('pointerdown', (event) => {
    const target = event.target as Node;
    if (!toggle.contains(target) && !panel.contains(target)) setOpen(false);
  });
}

function initMagneticElements(): void {
  if (reducedMotion || coarsePointer) return;

  selectAll<HTMLElement>('.magnetic').forEach((element) => {
    element.addEventListener('pointermove', (event) => {
      const rect = element.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width - 0.5) * 5;
      const y = ((event.clientY - rect.top) / rect.height - 0.5) * 4;
      gsap.to(element, { x, y, duration: 0.35, ease: 'power3.out', overwrite: 'auto' });
    });

    element.addEventListener('pointerleave', () => {
      gsap.to(element, { x: 0, y: 0, duration: 0.55, ease: 'power3.out', overwrite: 'auto' });
    });
  });
}

function initCursor(): void {
  if (reducedMotion || coarsePointer) return;

  const cursor = select<HTMLElement>('.custom-cursor');
  const dot = select<HTMLElement>('.cursor-dot');
  const ring = select<HTMLElement>('.cursor-ring');
  if (!cursor || !dot || !ring) return;

  let mouseX = window.innerWidth / 2;
  let mouseY = window.innerHeight / 2;
  let dotX = mouseX;
  let dotY = mouseY;
  let ringX = mouseX;
  let ringY = mouseY;
  let visible = false;

  window.addEventListener('pointermove', (event) => {
    mouseX = event.clientX;
    mouseY = event.clientY;
    if (!visible) {
      visible = true;
      cursor.classList.add('is-active');
      document.body.classList.add('has-custom-cursor');
    }
  });

  const render = (): void => {
    dotX += (mouseX - dotX) * 0.28;
    dotY += (mouseY - dotY) * 0.28;
    ringX += (mouseX - ringX) * 0.1;
    ringY += (mouseY - ringY) * 0.1;

    dot.style.transform = `translate3d(${dotX}px, ${dotY}px, 0) translate(-50%, -50%)`;
    ring.style.transform = `translate3d(${ringX}px, ${ringY}px, 0) translate(-50%, -50%)`;
    requestAnimationFrame(render);
  };
  requestAnimationFrame(render);

  selectAll<HTMLElement>('a, button').forEach((element) => {
    element.addEventListener('pointerenter', () => {
      cursor.classList.add('is-interactive');
      cursor.classList.toggle('is-card', element.dataset.cursor === 'card');
      cursor.classList.toggle('is-direction', element.dataset.cursor === 'direction');
      cursor.classList.toggle('is-inspect', element.dataset.cursor === 'inspect');
    });
    element.addEventListener('pointerleave', () => {
      cursor.classList.remove('is-interactive', 'is-card', 'is-direction', 'is-inspect');
    });
  });
}

interface Particle {
  x: number;
  y: number;
  radius: number;
  alpha: number;
  speed: number;
  drift: number;
  phase: number;
}

function initAtmosphere(): void {
  if (reducedMotion || window.matchMedia('(max-width: 760px)').matches) return;

  const canvas = select<HTMLCanvasElement>('#particle-field');
  const context = canvas?.getContext('2d');
  if (!canvas || !context) return;

  const particleCount = window.innerWidth < 1100 ? 16 : 26;
  const particles: Particle[] = [];
  let width = window.innerWidth;
  let height = window.innerHeight;
  let running = !document.hidden;

  const createParticle = (randomY = true): Particle => ({
    x: Math.random() * width,
    y: randomY ? Math.random() * height : height + 4,
    radius: 0.35 + Math.random() * 0.75,
    alpha: 0.05 + Math.random() * 0.11,
    speed: 0.08 + Math.random() * 0.16,
    drift: 0.15 + Math.random() * 0.35,
    phase: Math.random() * Math.PI * 2,
  });

  const resize = (): void => {
    width = window.innerWidth;
    height = window.innerHeight;
    const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
  };

  resize();
  for (let index = 0; index < particleCount; index += 1) {
    particles.push(createParticle());
  }

  const render = (): void => {
    if (running) {
      context.clearRect(0, 0, width, height);
      for (let index = 0; index < particles.length; index += 1) {
        const particle = particles[index];
        particle.phase += 0.006;
        particle.y -= particle.speed;
        particle.x += Math.sin(particle.phase) * particle.drift * 0.06;

        if (particle.y < -4) particles[index] = createParticle(false);

        context.beginPath();
        context.fillStyle = `rgba(225, 235, 241, ${particle.alpha})`;
        context.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        context.fill();
      }
    }
    requestAnimationFrame(render);
  };

  window.addEventListener('resize', resize, { passive: true });
  document.addEventListener('visibilitychange', () => {
    running = !document.hidden;
  });
  requestAnimationFrame(render);
}

function initPointerScene(): void {
  if (reducedMotion || coarsePointer) return;

  const hero = select<HTMLElement>('#hero');
  const atmosphere = select<HTMLElement>('#atmospheric-light');
  const copy = select<HTMLElement>('#hero-copy');
  const telemetry = select<HTMLElement>('.telemetry-layer');
  if (!hero || !atmosphere || !copy) return;

  let targetX = 0;
  let targetY = 0;
  let currentX = 0;
  let currentY = 0;

  hero.addEventListener('pointermove', (event) => {
    targetX = event.clientX / window.innerWidth - 0.5;
    targetY = event.clientY / window.innerHeight - 0.5;
  });

  gsap.ticker.add(() => {
    currentX += (targetX - currentX) * 0.045;
    currentY += (targetY - currentY) * 0.045;

    gsap.set(atmosphere, { x: currentX * 16, y: currentY * 16 });
    // The video owns physical camera movement. Pointer input only affects optical UI layers.
    gsap.set(copy, { x: currentX * 4 });
    if (telemetry) gsap.set(telemetry, { x: currentX * 3, y: currentY * 3 });
  });
}

function initCinematicIntro(): void {
  if (reducedMotion) return;

  const curtain = select<HTMLElement>('#startup-curtain');
  const nav = select<HTMLElement>('#hero-nav');
  const video = select<HTMLVideoElement>('#hero-video');
  const eyebrow = select<HTMLElement>('.hero-eyebrow');
  const title = select<HTMLElement>('#hero-title');
  const cta = select<HTMLElement>('.hero-cta');
  const leftStats = selectAll<HTMLElement>('.stats-left .stat-item');
  const rightStats = selectAll<HTMLElement>('.stats-right .stat-item');
  const statsColumns = selectAll<HTMLElement>('.stats-column');
  const dock = select<HTMLElement>('#feature-dock');
  const telemetry = select<HTMLElement>('.telemetry-layer');
  const scrollIndicator = select<HTMLElement>('#scroll-indicator');

  gsap.set(nav, { opacity: 0, y: -10 });
  gsap.set(eyebrow, { opacity: 0, y: 10, letterSpacing: '0.52em' });
  gsap.set(title, { opacity: 0, y: 22, filter: 'blur(8px)', letterSpacing: '0.08em' });
  gsap.set(cta, { opacity: 0, y: 12, filter: 'blur(5px)' });
  gsap.set(statsColumns, { opacity: 1 });
  gsap.set([...leftStats, ...rightStats], { opacity: 0, y: 13, '--line-scale': 0 });
  gsap.set(dock, { opacity: 0, y: 35, filter: 'blur(8px)' });
  gsap.set(telemetry, { opacity: 0 });
  gsap.set(scrollIndicator, { opacity: 0 });
  gsap.set(curtain, { opacity: 0.72 });

  let started = false;
  const launch = (): void => {
    if (started) return;
    started = true;

    const timeline = gsap.timeline({ defaults: { ease: 'power3.out' } });
    timeline
      .to(curtain, { opacity: 0.42, duration: 0.4, ease: 'power2.inOut' }, 0)
      .to(curtain, { opacity: 0, duration: 0.8, ease: 'power2.inOut' }, 0.38)
      .to(nav, { opacity: 1, y: 0, duration: 0.75 }, 0.45)
      .to(eyebrow, { opacity: 1, y: 0, letterSpacing: '0.42em', duration: 0.65 }, 0.7)
      .to(title, { opacity: 1, y: 0, filter: 'blur(0px)', letterSpacing: '0.015em', duration: 0.82 }, 0.9)
      .to(cta, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.58 }, 1.3)
      .to(leftStats, { opacity: 1, y: 0, '--line-scale': 1, duration: 0.56, stagger: 0.1 }, 1.5)
      .to(rightStats, { opacity: 1, y: 0, '--line-scale': 1, duration: 0.56, stagger: 0.1 }, 1.65)
      .to(dock, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.8 }, 1.9)
      .to(telemetry, { opacity: 1, duration: 0.7 }, 2.05)
      .to(scrollIndicator, { opacity: 1, duration: 0.65 }, 2.2)
      .call(initPointerScene, [], 2.7);
  };

  if (!video || video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    launch();
  } else {
    video.addEventListener('loadeddata', launch, { once: true });
    window.setTimeout(launch, 1200);
  }
}

interface VideoScrubber {
  setProgress: (progress: number) => void;
}

function createVideoScrubber(video: HTMLVideoElement): VideoScrubber {
  let duration = 0;
  let desiredTime = 0;
  let progress = 0;

  video.muted = true;
  video.playsInline = true;
  video.pause();

  const syncMetadata = (): void => {
    duration = Number.isFinite(video.duration) ? Math.max(0, video.duration - 0.05) : 0;
    desiredTime = duration * progress;
    video.pause();
    if (video.currentTime === 0 && duration > 0) video.currentTime = 0.001;
  };

  if (video.readyState >= HTMLMediaElement.HAVE_METADATA) syncMetadata();
  else video.addEventListener('loadedmetadata', syncMetadata, { once: true });

  const render = (): void => {
    if (
      duration > 0 &&
      !video.seeking &&
      Math.abs(video.currentTime - desiredTime) > 1 / 60
    ) {
      video.currentTime = desiredTime;
    }
    requestAnimationFrame(render);
  };
  requestAnimationFrame(render);

  // iOS permits deterministic seeking after the first muted, user-initiated media start.
  const primeTouchPlayback = (): void => {
    const resumeAt = desiredTime;
    const playPromise = video.play();
    if (!playPromise) return;
    void playPromise
      .then(() => {
        video.pause();
        desiredTime = resumeAt;
      })
      .catch(() => undefined);
  };
  window.addEventListener('touchstart', primeTouchPlayback, { once: true, passive: true });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) video.pause();
  });

  return {
    setProgress(nextProgress: number): void {
      progress = Math.min(1, Math.max(0, nextProgress));
      if (duration > 0) desiredTime = duration * progress;
    },
  };
}

function initScrollSequence(): void {
  if (reducedMotion) return;

  const hero = select<HTMLElement>('#hero');
  const video = select<HTMLVideoElement>('#hero-video');
  const heroCopy = select<HTMLElement>('#hero-copy');
  const nav = select<HTMLElement>('#hero-nav');
  const leftStats = select<HTMLElement>('.stats-left');
  const rightStats = select<HTMLElement>('.stats-right');
  const dock = select<HTMLElement>('#feature-dock');
  const indicator = select<HTMLElement>('#scroll-indicator');
  const telemetry = select<HTMLElement>('.telemetry-layer');
  const atmosphere = select<HTMLElement>('#atmospheric-light');
  const particles = select<HTMLElement>('#particle-field');
  const cinematicGrade = select<HTMLElement>('.cinematic-grade');
  const edgeVignette = select<HTMLElement>('.edge-vignette');
  const grain = select<HTMLElement>('.film-grain');
  const networkHandoff = select<HTMLElement>('#hero-network-handoff');
  const handoffLights = selectAll<HTMLElement>('.hero-handoff-light');
  if (!hero || !video) return;

  const scrubber = createVideoScrubber(video);
  const playhead = { progress: 0 };

  const timeline = gsap.timeline({
    defaults: { ease: 'power2.inOut' },
    scrollTrigger: {
      trigger: hero,
      start: 'top top',
      end: () => `+=${Math.round(window.innerHeight * (window.innerWidth <= 760 ? 4.2 : 4.8))}`,
      scrub: window.innerWidth <= 760 ? 0.25 : 0.4,
      pin: true,
      pinSpacing: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onLeave: () => scrubber.setProgress(1),
    },
  });

  timeline
    .to(playhead, {
      progress: 1,
      duration: 1,
      ease: 'none',
      onUpdate: () => scrubber.setProgress(playhead.progress),
    }, 0)
    .to(indicator, { opacity: 0, duration: 0.06, ease: 'none' }, 0)
    .to(dock, { y: 82, opacity: 0, duration: 0.2, ease: 'power2.in' }, 0.04)
    .to(heroCopy, { y: -58, opacity: 0, duration: 0.25 }, 0.08)
    .to(leftStats, { x: -58, opacity: 0, duration: 0.26 }, 0.1)
    .to(rightStats, { x: 58, opacity: 0, duration: 0.26 }, 0.1)
    .to(telemetry, { opacity: 0, duration: 0.2 }, 0.12)
    .to(nav, { y: -18, opacity: 0, duration: 0.23 }, 0.22)
    .to([atmosphere, particles], { opacity: 0, duration: 0.3 }, 0.24)
    .to(cinematicGrade, { opacity: 0.18, duration: 0.3 }, 0.5)
    .to(edgeVignette, { opacity: 0.32, duration: 0.3 }, 0.52)
    .to(grain, { opacity: 0.012, duration: 0.25 }, 0.55)
    .to(networkHandoff, { opacity: 1, duration: 0.085, ease: 'power2.inOut' }, 0.895)
    .to(handoffLights, { opacity: 1, duration: 0.045, ease: 'power2.out' }, 0.925)
    .to(handoffLights, { width: '12vw', duration: 0.055, ease: 'power2.out' }, 0.945);

  window.addEventListener(
    'scroll',
    () => {
      if (window.scrollY > window.innerHeight * 0.08 && indicator) {
        gsap.to(indicator, { opacity: 0, duration: 0.25, overwrite: 'auto' });
      }
    },
    { passive: true },
  );
}

function init(): void {
  initMenu();
  initMagneticElements();
  initCursor();
  initAtmosphere();
  initCinematicIntro();
  initScrollSequence();
  initEuropeNetwork({ reducedMotion, coarsePointer });
  initIntelligenceOnWheels({ reducedMotion, coarsePointer });
  initTerminalDeparture({ reducedMotion });
  initOriginDestination({ reducedMotion });
  initEnergySystem({ reducedMotion });
  initAlwaysMoving({ reducedMotion, coarsePointer });
  initMoveBeyondLimits({ reducedMotion });
  requestAnimationFrame(() => ScrollTrigger.refresh());
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
